import React, { useState, useEffect } from "react";
import { Coins, User, ChevronDown, Users } from "lucide-react";
import { useAuth } from "@/components/AuthContext";
import { toast } from "sonner";
import { fetchReferralInfo } from "@/services/customerService";
import { ThemedButton, ThemedCard, ThemedCardHeader } from "@/components/themed";
import type { ImageVariants } from "@/components/ResponsiveImage";

interface ThemeConfig {
  logo_type?: "text" | "image";
  logo_text?: string | null;
  logo_image_url?: string | null;
}

interface Site {
  id: number;
  name: string;
  subdomain: string;
  slug: string;
  description: string;
  icon_url: string | null;
  icon_variant_urls?: ImageVariants;
  thumbnail_url: string | null;
  privacy_url: string | null;
  terms_url: string | null;
  trade_law_url: string | null;
  created_at: string;
  identity_verified: boolean;
  antique_dealer_verified: boolean;
  delivery_count: number;
  site_type?: string;
  theme_config?: ThemeConfig;
}

interface HeaderProps {
  siteData?: Site;
}

const Header: React.FC<HeaderProps> = ({ siteData }) => {
  const { currentUser, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [referralEnabled, setReferralEnabled] = useState(false);

  useEffect(() => {
    if (currentUser) {
      fetchReferralInfo()
        .then((info) => setReferralEnabled(info.enabled))
        .catch(() => setReferralEnabled(false));
    }
  }, [currentUser]);

  const handleLogout = () => {
    logout();
    toast.success("ログアウトしました");
    setIsMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 w-full z-50 bg-theme-nav border-b-[length:var(--theme-border-width)] border-b-[color:var(--theme-border-color)]">
      <div className="flex h-14 md:h-16 items-center justify-between px-4 container mx-auto max-w-screen-lg">
        {/* ロゴ・店舗名 */}
        <a className="flex items-center group" href="/">
          <span className="sr-only">Home</span>
          {siteData?.theme_config?.logo_type === "image" && siteData?.theme_config?.logo_image_url ? (
            <img
              src={siteData.theme_config.logo_image_url}
              alt={siteData?.name || "JOYBASE"}
              className="h-8 md:h-10 max-w-[160px] md:max-w-[200px] object-contain"
            />
          ) : (
            <h1 className="text-base md:text-lg [font-weight:var(--theme-font-weight)] text-black group-hover:text-theme-primary transition-colors border-b-[length:var(--theme-border-width)] border-theme-primary">
              {siteData?.theme_config?.logo_text || siteData?.name || 'JOYBASE'}
            </h1>
          )}
        </a>

        {/* 右側メニュー */}
        <div className="flex items-center gap-2 md:gap-3">
          {currentUser ? (
            <>
              {/* 友達招待 */}
              {referralEnabled && (
                <ThemedButton
                  as-child
                  variant="secondary"
                  size="sm"
                  colorClass="bg-purple-400"
                  className="px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm text-black"
                  onClick={() => window.location.href = "/profile#referral"}
                  title="友達を招待してポイントをゲット"
                >
                  <Users size={14} className="md:w-4 md:h-4" />
                  <span className="hidden sm:inline whitespace-nowrap">招待</span>
                </ThemedButton>
              )}

              {/* ポイント */}
              <ThemedButton
                variant="primary"
                size="sm"
                className="px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm"
                onClick={() => window.location.href = "/point"}
              >
                <Coins size={14} className="md:w-4 md:h-4" />
                <span className="whitespace-nowrap">{currentUser.balance?.toLocaleString() || 0}pt</span>
              </ThemedButton>

              {/* アカウントメニュー */}
              <div
                className="relative"
                onMouseEnter={() => setIsMenuOpen(true)}
                onMouseLeave={() => setIsMenuOpen(false)}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                <ThemedButton variant="outline" size="sm" className="px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm">
                  <User size={14} className="md:w-4 md:h-4" />
                  <span className="hidden sm:inline">アカウント</span>
                  <ChevronDown size={12} className={`transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
                </ThemedButton>

                {/* ドロップダウンメニュー */}
                {isMenuOpen && (
                  <div className="absolute right-0 top-full pt-2 w-56 z-50">
                    <ThemedCard padding="none" className="overflow-hidden">
                      {/* ユーザー情報 */}
                      <ThemedCardHeader>
                        <p className="text-sm [font-weight:var(--theme-font-weight)] text-black truncate">{currentUser.email}</p>
                        <p className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-600 mt-1">
                          {currentUser.balance?.toLocaleString() || 0}pt 保有
                        </p>
                      </ThemedCardHeader>

                      {/* メニュー項目 */}
                      <nav className="py-1">
                        <a
                          href="/profile"
                          className="block px-4 py-2.5 text-sm [font-weight:var(--theme-font-weight-button)] text-black hover:bg-theme-primary/20 transition-colors"
                        >
                          マイページ
                        </a>
                        {referralEnabled && (
                          <a
                            href="/profile#referral"
                            className="flex items-center gap-2 px-4 py-2.5 text-sm [font-weight:var(--theme-font-weight-button)] text-purple-600 hover:bg-purple-50 transition-colors"
                          >
                            <Users size={14} />
                            友達招待
                          </a>
                        )}
                        <a
                          href="/home-address"
                          className="block px-4 py-2.5 text-sm [font-weight:var(--theme-font-weight-button)] text-black hover:bg-theme-primary/20 transition-colors"
                        >
                          お届け先住所
                        </a>
                        <a
                          href="/products"
                          className="block px-4 py-2.5 text-sm [font-weight:var(--theme-font-weight-button)] text-black hover:bg-theme-primary/20 transition-colors"
                        >
                          獲得した商品
                        </a>
                        {siteData?.site_type === 'kuji' && (
                          <a
                            href="/payment-methods"
                            className="block px-4 py-2.5 text-sm [font-weight:var(--theme-font-weight-button)] text-black hover:bg-theme-primary/20 transition-colors"
                          >
                            お支払い方法
                          </a>
                        )}
                        <a
                          href="/settings"
                          className="block px-4 py-2.5 text-sm [font-weight:var(--theme-font-weight-button)] text-black hover:bg-theme-primary/20 transition-colors"
                        >
                          設定
                        </a>
                      </nav>

                      {/* ログアウト */}
                      <div className="border-t-[length:var(--theme-border-width)] border-t-[color:var(--theme-border-color)]">
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-4 py-2.5 text-sm [font-weight:var(--theme-font-weight-button)] text-red-600 hover:bg-red-50 transition-colors"
                        >
                          ログアウト
                        </button>
                      </div>
                    </ThemedCard>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex gap-2">
              <ThemedButton
                variant="outline"
                size="sm"
                className="px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm"
                onClick={() => window.location.href = "/login"}
              >
                ログイン
              </ThemedButton>
              <ThemedButton
                variant="primary"
                size="sm"
                className="px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm"
                onClick={() => window.location.href = "/signup"}
              >
                会員登録
              </ThemedButton>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
